import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import Accordion from 'core/components/accordion/Accordion'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { LargeHangerIcon } from 'core/elements/sidebar/ItemHangerIcon'
import Grid from 'core/elements/grid'
import clsx from 'clsx'
import { keys, uniq } from 'ramda'
import DotValueCell from 'core/components/listTable/cells/DotValueCell'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { routeManager } from 'core/utils/routes'
import { IVolumeTypeDetailsPageTabs } from '../volume-types/details/model'

const useStyles = makeStyles<Theme>((theme) => ({
  accordionBody: {
    background: theme.palette.grey['000'],
    padding: 16,
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0px',
    gap: 16,
  },
  iconBox: {
    height: 40,
    width: 40,
    background: theme.palette.blue[150],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.blue[900]}`,
    borderRadius: 4,
  },
  icon: {
    color: theme.palette.blue[900],
  },
  sections: {
    display: 'grid',
    gap: 24,
  },
  sectionLabel: {
    color: theme.palette.grey[500],
  },
  section: {
    position: 'relative',
    marginLeft: 44,
    display: 'grid',
    gap: 8,
  },
  hangerBase: {
    backgroundColor: theme.palette.grey[300],
    height: 'calc(100% + 24px)',
    width: 1.1,
    position: 'absolute',
    left: -32,
    top: 0,
  },
  partialHangerBase: {
    backgroundColor: theme.palette.grey[300],
    height: 54,
    width: 1,
    position: 'absolute',
    left: -32,
    top: 0,
  },
  hanger: {
    position: 'absolute',
    left: -32,
    top: 0,
  },
  sameLine: {
    display: 'flex',
    gap: 24,
  },
}))

interface HeaderProps {
  title?: string | React.ReactNode
  description?: string | React.ReactNode
  className?: string
  icon: string
}

const AccordionHeader = ({ title, description, className, icon }: HeaderProps) => {
  const classes = useStyles()

  return (
    <div className={clsx(classes.accordionHeader, className)}>
      <div className={classes.iconBox}>
        <FontAwesomeIcon className={classes.icon}>{icon}</FontAwesomeIcon>
      </div>
      <div>
        <Text variant="subtitle2">{title}</Text>
        <Text variant="body2">{description}</Text>
      </div>
    </div>
  )
}

const volumeBackendColumns = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'driver',
    label: 'Driver',
  },
  {
    key: 'config',
    label: 'Key Value Configurations',
    render: (val) => keys(val)?.length,
  },
]

const dotColorMapFn = (value, palette) => {
  return value.includes('Attached') ? palette.green[700] : palette.yellow[700]
}

const VolumeTypeVolumesCell = ({ value, item }) => {
  const classes = useStyles()
  return (
    <div className={classes.sameLine}>
      <DotValueCell value={`Attached (${value?.active})`} dotColorFn={dotColorMapFn} />
      <DotValueCell value={`Detached (${value?.detached})`} dotColorFn={dotColorMapFn} />
    </div>
  )
}

const volumeTypeColumns = [
  {
    key: 'name',
    label: 'Volume Type Name',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id, name }) =>
        !!name
          ? routeManager.routes.openstack.volumeTypeDetails.path({
              id,
              tab: IVolumeTypeDetailsPageTabs.Overview,
            })
          : '',
    }),
  },
  {
    key: 'volumesInfo',
    label: 'Volumes',
    CellComponent: VolumeTypeVolumesCell,
  },
  {
    key: 'description',
    label: 'Description',
  },
]

export default function VolumeBackendCard({
  id,
  volumeBackend,
  volumeTypesByBackend,
  volumeTypes,
}) {
  const classes = useStyles()
  const theme = useTheme<Theme>()

  const volumeBackendConfigurations = volumeBackend?.configs || []

  const volumeTypesWithVolumes = useMemo(() => {
    const types = volumeTypesByBackend?.[volumeBackend?.name] || {}
    const typeNames = keys(types)
    return typeNames.reduce((accum, type) => {
      // Get the actual volumeType obj to get description, etc.
      const volumeType = volumeTypes.find((volType) => volType.name === type)
      const volumesInfo = types[type]?.reduce(
        (_accum, volume) => {
          if (!volume?.attachments?.length) {
            return {
              ..._accum,
              detached: _accum.detached + 1,
            }
          } else {
            const vmAttachment = volume.attachments.find((attachment) => !!attachment.server_id)
            const vmId = vmAttachment?.server_id
            return vmId && !_accum.vmIds.includes(vmId)
              ? {
                  ..._accum,
                  active: _accum.active + 1,
                  vmIds: [..._accum.vmIds, vmId],
                }
              : {
                  ..._accum,
                  active: _accum.active + 1,
                }
          }
        },
        {
          active: 0,
          detached: 0,
          vmIds: [],
        },
      )
      return [
        ...accum,
        {
          name: type,
          id: volumeType.id,
          description: volumeType.description,
          volumesInfo,
        },
      ]
    }, [])
  }, [volumeTypesByBackend, volumeBackend, volumeTypes])

  const totalVolumes = useMemo(() => {
    const totals = volumeTypesWithVolumes?.reduce(
      (accum, volumeType) => {
        return {
          active: accum.active + volumeType.volumesInfo.active,
          detached: accum.detached + volumeType.volumesInfo.detached,
        }
      },
      { active: 0, detached: 0 },
    )
    return totals.active + totals.detached
  }, [volumeTypesWithVolumes])

  const totalVms = useMemo(() => {
    const allVmIds = volumeTypesWithVolumes?.reduce((accum, volumeType) => {
      return uniq([...accum, ...volumeType.volumesInfo.vmIds])
    }, [])
    return allVmIds.length
  }, [volumeTypesWithVolumes])

  return (
    <Accordion
      hasNestedAccordion
      id={id}
      icon="chevron-down"
      iconRotate={180}
      title={
        <AccordionHeader
          title={volumeBackend?.name}
          icon="coins"
          // description="Physical storage system identifier"
        />
      }
    >
      <div className={classes.accordionBody}>
        <div className={classes.sections}>
          <div className={classes.section}>
            <Text className={classes.sectionLabel} variant="caption1">
              Volume Backend Configurations
            </Text>
            <div className={classes.hangerBase} />
            <LargeHangerIcon className={classes.hanger} fill={theme.palette.grey[300]} />
            <Accordion
              id={`${id}-backend-configs`}
              icon="chevron-down"
              iconRotate={180}
              title={
                <AccordionHeader
                  title={
                    volumeBackendConfigurations?.length === 1
                      ? `${volumeBackendConfigurations?.length} Volume Backend Configuration`
                      : `${volumeBackendConfigurations?.length} Volume Backend Configurations`
                  }
                  description="Connection configurations for this backend"
                  icon="gear"
                />
              }
            >
              <Grid
                uniqueIdentifier="name"
                data={volumeBackendConfigurations || []}
                columns={volumeBackendColumns}
                loading={false}
                label="Volume Backend Configurations"
                showItemsCountInLabel={false}
                compact
                disableToolbar
              />
            </Accordion>
          </div>
          <div className={classes.section}>
            <Text className={classes.sectionLabel} variant="caption1">
              Volume Types
            </Text>
            <div className={classes.partialHangerBase} />
            <LargeHangerIcon className={classes.hanger} fill={theme.palette.grey[300]} />
            <Accordion
              id={`${id}-volume-types`}
              icon="chevron-down"
              iconRotate={180}
              title={
                <AccordionHeader
                  title={
                    volumeTypesWithVolumes?.length === 1
                      ? `${volumeTypesWithVolumes?.length} Volume Type`
                      : `${volumeTypesWithVolumes?.length} Volume Types`
                  }
                  description={
                    <span>
                      {totalVolumes === 1 ? 'contains' : 'contain'} <b>{totalVolumes}</b>{' '}
                      {totalVolumes === 1 ? 'Volume' : 'Volumes'} which{' '}
                      {totalVolumes === 1 ? 'is' : 'are'} used across <b>{totalVms}</b>{' '}
                      {totalVms === 1 ? 'virtual machine' : 'virtual machines'}
                    </span>
                  }
                  icon="server"
                />
              }
            >
              <Grid
                uniqueIdentifier="name"
                data={volumeTypesWithVolumes || []}
                columns={volumeTypeColumns}
                loading={false}
                label="Volume Types"
                showItemsCountInLabel={false}
                compact
                disableToolbar
              />
            </Accordion>
          </div>
        </div>
      </div>
    </Accordion>
  )
}
